import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useMemo, useState, useCallback } from "react";

// =========================================
// DEV ENDPOINTS (hardcoded)
// =========================================
const API_BASE = apiUrl("");
const SUBSCRIPTIONS_ROUTE = "/dev/pwa_push_subscriptions"; // GET ?limit=...
const MESSAGES_ROUTE = "/dev/pwa_push_messages_logs"; // GET ?limit=...

// -----------------------------------------
// Small helpers
// -----------------------------------------
const fmtDateTime = dLike => {
  if (!dLike) return "-";
  const d = dLike instanceof Date ? dLike : new Date(dLike);
  if (isNaN(d)) return "-";
  return d.toLocaleString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};
const toStr = v => v === null || v === undefined ? "" : String(v);

// -----------------------------------------
// Styles (mirrors the table style in the example)
// -----------------------------------------
const styles = String.raw`
.pwa-dev-wrap { padding: 10px 0 24px; }
.hdr { display:flex; align-items:center; gap: 12px; flex-wrap: wrap; }
.hdr-right { margin-left: auto; display:flex; gap: 8px; align-items: end; }

.table-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 12px; }
@media (max-width: 1200px) { .table-pair { grid-template-columns: 1fr; } }

.data-card { border: 1px solid #d6dbe5; border-radius: 8px; overflow: hidden; background: #fff; }
.data-card .card-hdr { padding: 10px 12px; background: #FAFAFA; border-bottom: 1px solid #F0F0F0; display:flex; align-items:center; gap: 10px; }
.data-card .card-hdr h3 { margin: 0; font-size: 16px; font-weight: 700; }
.data-card .card-body { padding: 10px 12px; }

.data-table { width: 100%; border-collapse: collapse; table-layout: fixed; border: 1px solid #cfd8e3; }
.data-table th, .data-table td { border: 1px solid #d6dbe5; padding: 8px 10px; text-align: left; vertical-align: top; word-break: break-word; }
.data-table thead th { background: #EDF2F7; position: sticky; top: 0; z-index: 1; }
.data-table tbody tr:nth-child(odd) td { background-color: #fbfbfd; }

.pill { display:inline-block; padding: 2px 8px; border-radius: 999px; font-size: 12px; border: 1px solid #D9D9D9; }
.pill.ok { background: #e6ffed; border-color: #B7EB8F; }
.pill.bad { background: #ffe6e6; border-color: #FFA39E; }

.ctrls { display:flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.ctrls label { display:flex; gap: 6px; align-items: center; }
.ctrls input[type="number"] { width: 100px; }
.ctrls input[type="text"] { width: 220px; }
.btn { padding: 6px 10px; border: 1px solid #D9D9D9; border-radius: 6px; background: #fff; cursor: pointer; }
.btn.primary { background: #0E8CFF; color: #fff; border-color: #0958D9; }
`;

// -----------------------------------------
// Component
// -----------------------------------------
const PwaDev = () => {
  // data state
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);

  // ui state
  const [limitSubs, setLimitSubs] = useState(200);
  const [limitMsgs, setLimitMsgs] = useState(200);
  const [searchSubs, setSearchSubs] = useState("");
  const [searchMsgs, setSearchMsgs] = useState("");
  const [onlySuccess, setOnlySuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const authToken = useMemo(() => localStorage.getItem("authToken") || localStorage.getItem("token"), []);
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    ...(authToken ? {
      Authorization: `Bearer ${authToken}`
    } : {})
  }), [authToken]);
  const fetchSubs = useCallback(async (limit = 200) => {
    const url = `${API_BASE}${SUBSCRIPTIONS_ROUTE}?limit=${encodeURIComponent(limit)}`;
    const res = await fetch(url, {
      method: "GET",
      headers
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`Subscriptions fetch failed ${res.status}: ${text}`);
    const json = text ? JSON.parse(text) : {
      items: []
    };
    return Array.isArray(json.items) ? json.items : [];
  }, [headers]);
  const fetchMsgs = useCallback(async (limit = 200) => {
    const url = `${API_BASE}${MESSAGES_ROUTE}?limit=${encodeURIComponent(limit)}`;
    const res = await fetch(url, {
      method: "GET",
      headers
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`Messages fetch failed ${res.status}: ${text}`);
    const json = text ? JSON.parse(text) : {
      items: []
    };
    return Array.isArray(json.items) ? json.items : [];
  }, [headers]);
  const refresh = useCallback(async () => {
    setLoading(true);
    setErrorText("");
    try {
      const [s, m] = await Promise.all([fetchSubs(limitSubs), fetchMsgs(limitMsgs)]);
      setSubs(s);
      setMsgs(m);
    } catch (e) {
      console.error("PWA DEV fetch error:", e);
      setErrorText(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [fetchSubs, fetchMsgs, limitSubs, limitMsgs]);
  useEffect(() => {
    refresh();
  }, []); // initial load once

  // ---------------------------------------
  // Filtering
  // ---------------------------------------
  const filteredSubs = useMemo(() => {
    const q = searchSubs.trim().toLowerCase();
    if (!q) return subs;
    return subs.filter(row => Object.values(row || {}).some(v => toStr(v).toLowerCase().includes(q)));
  }, [subs, searchSubs]);
  const filteredMsgs = useMemo(() => {
    const q = searchMsgs.trim().toLowerCase();
    return msgs.filter(row => {
      if (onlySuccess && row?.success !== true) return false;
      if (!q) return true;
      return Object.values(row || {}).some(v => toStr(v).toLowerCase().includes(q));
    });
  }, [msgs, searchMsgs, onlySuccess]);

  // ---------------------------------------
  // Render
  // ---------------------------------------
  const generatedAt = fmtDateTime(new Date());
  return <div className="pwa-dev-wrap">
      <style>{styles}</style>

      <div className="hdr">
        <h2 style={{
        margin: 0
      }}>PWA Push (DEV)</h2>
        <div className="hdr-right">
          <button className="btn" onClick={refresh} disabled={loading}>{loading ? "Refreshing..." : "Refresh"}</button>
        </div>
      </div>

      <div style={{
      marginTop: 6,
      fontSize: 12,
      color: "#434343"
    }}>
        Generated: {generatedAt}
      </div>

      {errorText && <div style={{
      marginTop: 8,
      padding: "8px 10px",
      background: "#FFF1F0",
      border: "1px solid #FFCCC7",
      borderRadius: 6
    }}>
          <strong>Error:</strong> {errorText}
        </div>}

      <div className="table-pair">
        {/* Subscriptions */}
        <div className="data-card">
          <div className="card-hdr">
            <h3>Push Subscriptions</h3>
            <span className="pill">{filteredSubs.length} shown</span>
            <div className="ctrls" style={{
            marginLeft: "auto"
          }}>
              <label>
                Limit
                <input type="number" min={1} max={2000} value={limitSubs} onChange={e => setLimitSubs(Math.max(1, Math.min(2000, parseInt(e.target.value || "0", 10))))} />
              </label>
              <label>
                Search
                <input type="text" placeholder="Search..." value={searchSubs} onChange={e => setSearchSubs(e.target.value)} />
              </label>
              <button className="btn" onClick={() => refresh()}>Apply</button>
            </div>
          </div>
          <div className="card-body" style={{
          padding: 0
        }}>
            <div style={{
            maxHeight: 600,
            overflow: "auto"
          }}>
              <table className="data-table">
                <colgroup>
                  <col style={{
                  width: "200px"
                }} />
                  <col style={{
                  width: "180px"
                }} />
                  <col style={{
                  width: "200px"
                }} />
                  <col style={{
                  width: "120px"
                }} />
                  <col style={{
                  width: "100px"
                }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>_id</th>
                    <th>createdAt</th>
                    <th>patientId</th>
                    <th>platform</th>
                    <th>isActive</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.length === 0 && <tr><td colSpan={5}>No data.</td></tr>}
                  {filteredSubs.map((r, i) => <tr key={r._id?.$oid || r._id || i}>
                      <td>{toStr(r._id?.$oid || r._id)}</td>
                      <td>{fmtDateTime(r.createdAt)}</td>
                      <td>{toStr(r.patientId?.$oid || r.patientId)}</td>
                      <td>{toStr(r.platform)}</td>
                      <td>{r.isActive === true ? <span className="pill ok">true</span> : <span className="pill bad">false</span>}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="data-card">
          <div className="card-hdr">
            <h3>Push Message Logs</h3>
            <span className="pill">{filteredMsgs.length} shown</span>
            <div className="ctrls" style={{
            marginLeft: "auto"
          }}>
              <label>
                Limit
                <input type="number" min={1} max={2000} value={limitMsgs} onChange={e => setLimitMsgs(Math.max(1, Math.min(2000, parseInt(e.target.value || "0", 10))))} />
              </label>
              <label>
                Success only
                <input type="checkbox" checked={onlySuccess} onChange={e => setOnlySuccess(e.target.checked)} />
              </label>
              <label>
                Search
                <input type="text" placeholder="Search..." value={searchMsgs} onChange={e => setSearchMsgs(e.target.value)} />
              </label>
              <button className="btn" onClick={() => refresh()}>Apply</button>
            </div>
          </div>
          <div className="card-body" style={{
          padding: 0
        }}>
            <div style={{
            maxHeight: 600,
            overflow: "auto"
          }}>
              <table className="data-table">
                <colgroup>
                  <col style={{
                  width: "200px"
                }} />
                  <col style={{
                  width: "160px"
                }} />
                  <col style={{
                  width: "180px"
                }} />
                  <col style={{
                  width: "180px"
                }} />
                  <col style={{
                  width: "110px"
                }} />
                  <col style={{
                  width: "260px"
                }} />
                  <col style={{
                  width: "200px"
                }} />
                  <col style={{
                  width: "110px"
                }} />
                  <col style={{
                  width: "240px"
                }} />
                  <col style={{
                  width: "160px"
                }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>_id</th>
                    <th>createdAt</th>
                    <th>patientId</th>
                    <th>subscriptionId</th>
                    <th>directionId</th>
                    <th>subject</th>
                    <th>content</th>
                    <th>success</th>
                    <th>response</th>
                    <th>receivedAt</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMsgs.length === 0 && <tr><td colSpan={10}>No data.</td></tr>}
                  {filteredMsgs.map((r, i) => <tr key={r._id?.$oid || r._id || i}>
                      <td>{toStr(r._id?.$oid || r._id)}</td>
                      <td>{fmtDateTime(r.createdAt)}</td>
                      <td>{toStr(r.patientId?.$oid || r.patientId)}</td>
                      <td>{toStr(r.subscriptionId?.$oid || r.subscriptionId)}</td>
                      <td>{toStr(r.directionId)}</td>
                      <td>{toStr(r.subject)}</td>
                      <td>{toStr(r.content)}</td>
                      <td>{r.success === true ? <span className="pill ok">true</span> : <span className="pill bad">false</span>}</td>
                      <td>{toStr(r.response)}</td>
                      <td>{fmtDateTime(r.receivedAt)}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default PwaDev;
